import {AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  NegocioDataService,
  dateToMonthAndYearFormat,
  NEG_TYPE_DISPO,
  NEG_TYPE_FORWARD,
  NEG_TYPE_PAG_DIFE
} from '@services/negocio-data.service';
import {Subscription} from 'rxjs/Subscription';
import {
  AcopioViewModel,
  ClienteSaldoViewModel,
  ClienteViewModel,
  CosechaViewModel,
  NegocioSaveViewModel,
  NegocioViewModel,
  PosicionViewModel
} from '@utils/ViewModels';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {FormSelectItem, FormSelectMultipleComponent, FormSelectMultipleItem, FormSelectSingleComponent} from '@common/form-components';
import {Observable} from 'rxjs/Observable';
import {of} from 'rxjs/observable/of';
import {SEARCH_TYPE_COLABORADOR, SearchService} from '@services/search-service';
import * as moment from 'moment';
import {MD_DIALOG_DATA, MdDialog, MdDialogRef, MdSnackBar} from '@angular/material';
import {ColaboradoresService} from '@services/colaboradores-service';
import {Subject} from 'rxjs/Subject';
import {interval} from 'rxjs/observable/interval';
import {DOLAR_PESOS_ID} from '@services/moneda-service';
import {NegociosService} from '@services/negocios-service';
import {CommonService} from '@services/common-service';
import {CuentasService} from '@services/cuentas-service';
import {ProductoService} from '@services/producto-service';
import {TextDialogComponent} from '@common/dialog';
import {Breakpoints, BreakpointsObserverService} from '@services/breakpoints-observer.service';
import {Precio} from '../../precios-seccion/general-interfaces';
import {TnsPipe} from '@utils/pipes';
import { convertToThousands } from '@utils/formatter';

export const DOLAR_ID = 2;
export const NEG_OLVIDADO_ID = 6;
export const ENT_FORWARD_ID = 3;
export const ENT_PAGO_DIFERIDO_ID = 4;

@Component({
  selector: 'app-new-negocio-responsive-dialog',
  templateUrl: './new-negocio-responsive-dialog.component.html',
  styleUrls: ['./new-negocio-responsive-dialog.component.scss']
})
export class NewNegocioResponsiveDialogComponent implements AfterViewInit, OnDestroy {
  form: FormGroup;
  subscription = new Subscription();

  isClone = false;
  isEdit = false;
  // existe un bug en form que previene el definir valores en los controles sin emitir eventos
  // usamos esta flag para los casos en que haya un conflicto por un evento emitido.
  // verificar esto una vez se actualize a angular 14 con typed forms
  negocioLoading = false;
  submitLoading = false;
  negocioBase!: NegocioViewModel;
  firstIteration = true;
  currentColab = Number(localStorage.getItem('userId'));

  today = moment().format('DD/MM/YYYY');
  maxExpiration =  moment().add(14, 'days').toDate();
  maxVencimiento = moment().add(14, 'days').format('DD/MM/YYYY');
  ayerString = moment().add(-1, 'days').format('DD/MM/YYYY'); // usado para la fecha maxima de olvido

  // creamos el control de entrega y posicion separados para evitar nullchecks
  clienteCtrl = new FormControl(null, [Validators.required]);
  tipoCtrl = new FormControl(null, Validators.required);
  negocioCtrl = new FormControl(null, Validators.required);
  entregaCtrl = new FormControl({value: null, disabled: true}, Validators.required);
  vencimientoCtrl = new FormControl(this.maxExpiration);
  observacionCtrl = new FormControl('');
  productoCtrl = new FormControl(null, Validators.required);
  colaboradoresCtrl = new FormControl([], null, this.getAsyncValidarProductos(this.productoCtrl, this.productoService));
  acopioCtrl = new FormControl({value: null, disabled: true}, Validators.required);
  mercadoCtrl = new FormControl(null, Validators.required);
  cosechaCtrl = new FormControl(null, Validators.required);
  toneladasCtrl = new FormControl(null, [Validators.required, (c) => this.validateNumbers(c, true)]);
  monedaCtrl = new FormControl(null, Validators.required);
  baseCtrl = new FormControl(null, [Validators.required, this.validateNumbers]);
  responsableCtrl = new FormControl({value: null, disabled: true});
  fechaOlvidoCtrl = new FormControl(null);

  validateNumbers(control: AbstractControl, isTns = false) {
    const value = control.value;
    if (!value) return null;

    const numericValue = parseFloat(value.replace(/[^0-9,]/g, '').replace(',','.'));
    return isNaN(numericValue) || (isTns ? numericValue <= 0 : numericValue < 1) ? {error: true} : null; 
  }

  NEG_TYPE_DISPO_tmp = NEG_TYPE_DISPO;

  @ViewChild('clienteFormComp') clienteFormComp!: FormSelectSingleComponent;
  public $clienteOptions = of<FormSelectItem[]>([]);

  public $acopioOptions = of<FormSelectItem[]>([]);

  @ViewChild('colabFormComp') colabFormComp!: FormSelectMultipleComponent;
  public $colaboradorOptions = of<FormSelectItem[]>([]);

  public $entregaOptions = of<FormSelectItem[]>([]);

  public $saldos: Observable<ClienteSaldoViewModel[]> = of<ClienteSaldoViewModel[]>([]);
  public saldosOpen = false;
  @ViewChild('saldosTable') saldosTableElem!: ElementRef;
  public saldosLoading = false;

  /**
   * Esta flag nos indica si es primer dia habil del mes, en caso de serlo, el listado de posiciones nos debe permitir
   * crear un nuevo negocio con una posicion de este mes, caso contrario no se pueden crear neg con posiciones de este mes
   */
  firstBusinessDay = false;

  showOlvidado = false;
  confirmButtonText = 'Generar Negocio';
  destroySubject = new Subject<void>();

  mercadoInitialized = false;
  $mercadoOptions = this.negocioDataService.$mercadosOptionsAsForm.do(x => {
    if ((!this.data || (!this.data.isClone && !this.data.isEdit)) && !this.mercadoInitialized && x.length) {
      // si estamos creando un nuevo negocio y es el primer valor emitido por el observable, seteamos el primer mercado por defecto
      this.mercadoCtrl.setValue(x[0]);
      this.mercadoInitialized = true;
    }
  });

  isMobile = false;
  cuentaAcopioError = false;

  protected readonly window = window;

  constructor(
    private fb: FormBuilder,
    private searchService: SearchService,
    private negocioService: NegociosService,
    private userService: ColaboradoresService,
    private productoService: ProductoService,
    @Inject(MD_DIALOG_DATA) public data: NewNegocioData,
    private dialog: MdDialog,
    private commonService: CommonService,
    public negocioDataService: NegocioDataService,
    private cuentasService: CuentasService,
    private snackbar: MdSnackBar,
    private breakpoints: BreakpointsObserverService,
    private dialogRef: MdDialogRef<NewNegocioResponsiveDialogComponent>,
    private tnsPipe: TnsPipe,
  ) {
    breakpoints.observe([Breakpoints.XSmall, Breakpoints.Small]).takeUntil(this.destroySubject).subscribe(x => {
      this.isMobile = x.matches;
    });

    this.form = fb.group({
      cliente: this.clienteCtrl,
      acopio: this.acopioCtrl,
      tipo: this.tipoCtrl, // orden intencion olvidado
      negocio: this.negocioCtrl, // dispo fw pag dif
      entrega: this.entregaCtrl, // posicion o entrega
      mercado: this.mercadoCtrl,
      producto: this.productoCtrl,
      cosecha: this.cosechaCtrl,
      toneladas: this.toneladasCtrl,
      moneda: this.monedaCtrl,
      base: this.baseCtrl,
      vencimiento: this.vencimientoCtrl,
      colaboradores: this.colaboradoresCtrl,
      observacion: this.observacionCtrl,
      fechaOlvido: this.fechaOlvidoCtrl,
      responsable: this.responsableCtrl,
    });

    if (this.data && this.data.isEdit || this.data.isClone || this.data.fromPrecio !== null) {
      // en caso de que haya datos, debemos preparar el formulario con todos los datos que nos llegan
      // verificar tambien si se trata de una clonacion o una edicion
      this.isEdit = data.isEdit === true;
      this.isClone = data.isClone === true;
      if (this.data.fromPrecio === null) {
        this.confirmButtonText = this.isEdit ? 'Editar Negocio' : 'Duplicar Negocio';
      }
      this.negocioLoading = true;
      // buscamos el negocio, pero antes bloqueamos el formulario
      // TODO sistema de bloqueo de form con memoria de que campos estaban deshabilitados antes
      // dado que para setear un negocio necesitamos SI O SI las posiciones y entregas, nos suscribimos a un observable
      // usamos combinelatest para verificar ambos observables y solo continuamos si existe valor en ambos observables
      // el takuntil lo usamos para detener la ejecucion de combinelatest cuando ambos observables tengan valores
      const latestEnded = new Subject<boolean>();
      Observable.combineLatest([this.negocioDataService.$posicionAll, this.negocioDataService.$entregaDispo], (...results: any[]) => {
        return {posicion: results[0], entregas: results[1]};
      }).takeUntil(latestEnded).subscribe(x => {
        if (x.posicion.length && x.entregas.length) {
          // hay valores, disparamos el next para que se desuscriba combinelatest
          latestEnded.next(true);
          // verificamos si es negocio o desde precio
          if (this.data.fromPrecio !== null) {
            this.initalizeFormFromPrecio(this.data.fromPrecio);
          } else if (this.data.idNegocio !== 0) {
            // buscamos el negocio segun su id
            this.negocioService.getNegocio(data.idNegocio).take(1).subscribe(neg => {
              this.cuentasService.getCuenta(String(neg.ResponseData.Cliente.Id)).take(1).subscribe(cliente => {
                neg.ResponseData.Cliente = cliente;
                this.initializeFormFromData(neg.ResponseData);
                this.negocioBase = neg.ResponseData;
              })
            });
          }
        }
      });
    }
    if (!this.data || (this.data && !this.data.isEdit && !this.data.isClone)) {
      // inicializamos el responsable
      this.userService.getColaborador(Number(localStorage.getItem('userId'))).take(1).subscribe(x => {
        const colab: FormSelectItem = {Label: x.UserFullName, Id: x.UserId, RawValue: JSON.stringify(x)};
        this.responsableCtrl.setValue(colab);
      });      
    }

    this.subscription.add(
      // creamos un timer que actualiza las posiciones del servicio cada 1 minuto en caso de que cambien
      interval(60000).timeInterval().takeUntil(this.destroySubject).subscribe(() => {
        this.negocioDataService.updatePosicion();
      })
    );

    this.negocioDataService.updatePosicion(); // solicitamos la ultima actualizacion de posiciones

    // actualizar los valores de entrega
    this.subscription.add(
      this.negocioCtrl.valueChanges.subscribe(value => {
        this.entregaCtrl.enable();
        // limpiamos el valor solo si no se está cargando el form
        if (!this.negocioLoading) {
          this.entregaCtrl.setValue(null);
        }
        this.updateEntregaOptions();
        // en caso de ser un negocio disponible, y estar seleccionado el dolar a pesificar
        // lo deseleccionamos puer solo existe en disponible
        if (value !== NEG_TYPE_DISPO && (this.monedaCtrl.value && this.monedaCtrl.value.Id === DOLAR_PESOS_ID)) {
          this.monedaCtrl.setValue(null);
        }
      })
    );

    // verificamos si es el primer dia habil del mes
    this.subscription.add(
      this.commonService.getFirstBusinessDay().take(1).subscribe(x => {
        this.firstBusinessDay = moment().startOf('day').isSame(x, 'day'); // Granularidad a nivel año, mes, y día
        this.updateEntregaOptions();
      })
    );

    // al cambiar el valor de tipo (orden, intencion, olvidado)
    this.subscription.add(
      this.tipoCtrl.valueChanges.subscribe(x => {
        // flag para saber si es negocio olvidado
        this.showOlvidado = x.Id === NEG_OLVIDADO_ID;
        // para negocios olvidados, seteamos la fecha de vencimiento a HOY, de lo contrario limpiamos el vencimiento
        // el campo observacion requiere un valor en caso de ser negocio olvidado
        if (x.Id === NEG_OLVIDADO_ID) {
          if ((this.isEdit || this.isClone) && !this.firstIteration) {
            this.vencimientoCtrl.setValue(moment().toDate());
            this.vencimientoCtrl.disable();
          }
          this.observacionCtrl.setValidators(Validators.required);
          this.observacionCtrl.updateValueAndValidity();
          this.fechaOlvidoCtrl.setValidators(Validators.required);
          this.fechaOlvidoCtrl.updateValueAndValidity();
        } else {
          if ((this.isEdit || this.isClone) && !this.firstIteration) {
            this.vencimientoCtrl.setValue(this.maxExpiration);
            this.vencimientoCtrl.enable();
          }
          this.observacionCtrl.clearValidators();
          this.observacionCtrl.updateValueAndValidity();
          this.fechaOlvidoCtrl.clearValidators();
          this.fechaOlvidoCtrl.updateValueAndValidity();
        }
      })
    );

    // al seleccionar un cliente podemos acceder al selector de acopio
    this.$acopioOptions = this.clienteCtrl.valueChanges
      // switchmap para pasar al observable de la busqueda, desactivamos el control en caso de que no haya cliente
      .switchMap(val => {
        if (!val) {
          this.acopioCtrl.disable();
          return of<AcopioViewModel[]>([]);
        }        
        if ((!this.isEdit && !this.isClone) || ((this.isEdit || this.isClone) && !this.firstIteration)) {
          this.acopioCtrl.setValue(null);
        }
        return this.negocioService.getAcopiosByCliente(this.clienteCtrl.value.Id);
      })
      // lo convertimos en formSelect
      .map(acos => {
        return acos.reduce((acc, acopio) => {
          if (!acc.find(item => item.Id === acopio.Id)) {
            acc.push({Id: acopio.Id, Label: acopio.AcopioNombre, RawValue: JSON.stringify(acopio)});
          }

          return acc;
        }, [] as FormSelectItem[])
      })
      // preseleccionamos la primer opcion
      .do(val => {
        this.acopioCtrl.enable();
        // en caso de estar en modo de edicion o de clonacion, no se deben preseleccionar
        if (val.length && !(this.isEdit || this.isClone)) {
          this.acopioCtrl.setValue(val[0]);
        }

        this.checkCuentaAcopioValid();
      });

    // cuando cambia el producto se debe revalidar el estado de los colaboradores para verificar si hay error
    this.subscription.add(
      this.productoCtrl.valueChanges.subscribe(x => {
        if (x) {
          this.colaboradoresCtrl.updateValueAndValidity();
        }
      })
    );

    this.$saldos = this.acopioCtrl.valueChanges
      .switchMap(x => {
        this.saldosLoading = true;
        if (!x || !this.clienteCtrl.value) {
          return of([]);
        } else {
          return this.cuentasService.getClienteSaldo({AcopioId: x.Id, ClienteId: this.clienteCtrl.value.Id});
        }
      })
      .catch(() => {
        this.snackbar.open('Ha ocurrido un error al obtener los saldos', 'Aceptar', {duration: 5000});
        this.saldosLoading = false;
        return of([]);
      })
      .do(() => {
        this.saldosLoading = false;
        this.checkCuentaAcopioValid();
      });
    }

  ngOnDestroy() {
    this.destroySubject.next();
  }

  /**
   * Inicializamos el formulario a partir de un negocio. Mucho cuidado con los eventos
   * de cambio de datos, no queremos disparar ninguno para evitar que se ejecute lógica intermedia.
   * */
  initializeFormFromData(neg: NegocioViewModel) {
    const opts = {emitEvent: false, onlySelf: true};
    this.clienteCtrl.setValue({Id: neg.Cliente.Id, Label: neg.Cliente.FullName, RawValue: JSON.stringify(neg.Cliente)});
    this.acopioCtrl.setValue({Id: neg.Acopio.Id, Label: neg.Acopio.AcopioNombre, RawValue: JSON.stringify(neg.Acopio)}, opts);
    this.mercadoCtrl.setValue({Id: neg.Mercado.Id, Label: neg.Mercado.Mercado, RawValue: JSON.stringify(neg.Mercado)}, opts);
    this.productoCtrl.setValue({Id: neg.Producto.Id, Label: neg.Producto.Producto, RawValue: JSON.stringify(neg.Producto)}, opts);
    this.cosechaCtrl.setValue({Id: neg.Cosecha.Id, Label: neg.Cosecha.Cosecha, RawValue: JSON.stringify(neg.Cosecha)}, opts);
    this.toneladasCtrl.setValue(convertToThousands(neg.Toneladas, true), opts);
    this.monedaCtrl.setValue({Id: neg.Moneda.Id, Label: neg.Moneda.Moneda, RawValue: JSON.stringify(neg.Moneda)}, opts);
    this.baseCtrl.setValue(convertToThousands(neg.Base, true), opts);
    this.responsableCtrl.setValue(
      {Id: neg.Responsable.UserId, Label: neg.Responsable.UserFullName, RawValue: JSON.stringify(neg.Responsable)}, opts
    );
    this.colaboradoresCtrl.setValue(
      neg.Colaboradores
      .filter(col => col.UserId !== neg.Responsable.UserId)
      .map(col => ({Id: col.UserId, Label: col.UserFullName, RawValue: JSON.stringify(col)})), opts
    );
    this.tipoCtrl.setValue({Id: neg.TipoNegocio.Id, Label: neg.TipoNegocio.Tipo, RawValue: JSON.stringify(neg.TipoNegocio)});
    this.vencimientoCtrl.setValue(moment.unix(neg.Vencimiento).toDate());
    this.observacionCtrl.setValue(neg.Observaciones);
    // para negocios olvidados, deshabilitamos el selector de tipo, lo hacemos aqui para mantener el disabled state
    if (neg.TipoNegocio.Id === NEG_OLVIDADO_ID) {
      this.fechaOlvidoCtrl.setValue(moment(neg.Fecha).toDate());
      this.tipoCtrl.disable();
    }

    // dado que utilizamos el source de posiciones, nos suscribimos al mismo para tener la data completamente actualizada
    // usamos un forkjoin para tener la data de forma sincronica entre los dos observables
    // dado que ya verificamos que los observables tengan datos, podemos usar un take 1 sin miedo para completar el observable
    Observable.forkJoin([this.negocioDataService.$posicionAll.take(1), this.negocioDataService.$entregaDispo.take(1)])
      .take(1)
      .subscribe($data => {
        const posiciones = $data[0];
        const entregas = $data[1];

        if (neg.Entrega.EsPosicion || neg.Entrega.Id === ENT_PAGO_DIFERIDO_ID) {
          // creamos el valor del negocioCtrl a partir de los mismos datos que en negocioDataService
          this.negocioCtrl.setValue(
            neg.Entrega.EsPosicion ? {Id: NEG_TYPE_FORWARD, Label: NEG_TYPE_FORWARD} :
              {Id: NEG_TYPE_PAG_DIFE, Label: NEG_TYPE_PAG_DIFE}, {emitEvent: false}
          );
          // en caso de ser diferido o posicion, podemos usar el mismo creador de objetos
          const p = posiciones.find(x => x.Id === neg.NegocioTipoPosicion.Id);
          if (p) {
            // EsPosicion es true para forward y falso para diferido, solo queremos convertir la fecha para forward
            this.entregaCtrl.setValue(this.posicionToFormObj(p, neg.Entrega.EsPosicion), opts);
          }
        } else {
          // disponible
          this.negocioCtrl.setValue({Id: NEG_TYPE_DISPO, Label: NEG_TYPE_DISPO}, opts);
          const e = entregas.find(x => x.Id === neg.Entrega.Id);
          if (e) {
            this.entregaCtrl.setValue({Id: e.Id, Label: e.Entrega}, opts);
          }
        }
        this.entregaCtrl.enable();
        // luego de definir los tipos, reactivamos el formulario
        // TODO sistema de deshabilitado de form
        // antes de habilitar los eventos usamos un timeout porque los eventos se disparan en el siguiente ciclo
        window.setTimeout(() => {
          this.negocioLoading = false;
        }, 50);
      });

    Object.keys(this.form.controls).forEach(ctrl => {
      const control = this.form.get(ctrl);
      if (control) {
        control.updateValueAndValidity();
      }
    });
    this.firstIteration = false;
  }

  initalizeFormFromPrecio(precio: Precio) {
    const opts = {emitEvent: false, onlySelf: true};
    this.baseCtrl.setValue(precio.Precio.toString(), opts);
    this.baseCtrl.updateValueAndValidity();
    // dado que para setear un negocio necesitamos SI O SI los datos, nos suscribimos a un observable
    // usamos combinelatest para verificar los observables y solo continuamos si existe valor en los observables
    // el takuntil lo usamos para detener la ejecucion de combinelatest cuando ambos observables tengan valores
    const latestEnded = new Subject<boolean>();
    // dado que utilizamos los sources, nos suscribimos a los mismos para tener la data completamente actualizada
    Observable.combineLatest([
      this.negocioDataService.$posicionAll,
      this.negocioDataService.$entrega,
      this.$mercadoOptions,
      this.negocioDataService.$monedaOptionsAsForm,
      this.negocioDataService.$productosOptionsAsForm,
    ],
      (...results: any[]) => {
      return {posicion: results[0], entregas: results[1], mercados: results[2], monedas: results[3], productos: results[4]};
    }).takeUntil(latestEnded).subscribe(res => {
      if (res.entregas.length && res.posicion.length && res.mercados.length && res.monedas.length && res.productos.length) {
        latestEnded.next();

        this.mercadoCtrl.setValue(res.mercados.find(x => x.Id === precio.PrecioRAW.Mercado.Id));
        this.mercadoCtrl.updateValueAndValidity();
        this.productoCtrl.setValue(res.productos.find(x => x.Id === precio.PrecioRAW.Producto.Id));
        this.productoCtrl.updateValueAndValidity();
        this.monedaCtrl.setValue(res.monedas.find(x => x.Id === precio.PrecioRAW.Moneda.Id));
        this.monedaCtrl.updateValueAndValidity();
        // verificamos el tipo de negocio
        // Entrega/Posición y Negocio
        const isPosicion = precio.PrecioRAW.Entrega.Id === 3;
        const isPagoDiferido = precio.PrecioRAW.Entrega.Id === 4;

        if (isPosicion || isPagoDiferido) {
          // creamos el valor del negocioCtrl a partir de los mismos datos que en negocioDataService
          this.negocioCtrl.setValue(
            isPosicion ? {Id: NEG_TYPE_FORWARD, Label: NEG_TYPE_FORWARD} :
              {Id: NEG_TYPE_PAG_DIFE, Label: NEG_TYPE_PAG_DIFE}, {emitEvent: false}
          );
          // en caso de ser diferido o posicion, podemos usar el mismo creador de objetos
          const p = res.posicion.find(x => x.Id === precio.PrecioRAW.Posicion.Id);
          if (p) {
            // EsPosicion es true para forward y falso para diferido, solo queremos convertir la fecha para forward
            this.entregaCtrl.setValue(this.posicionToFormObj(p, isPosicion), opts);
          }
        } else {
          // disponible
          this.negocioCtrl.setValue({Id: NEG_TYPE_DISPO, Label: NEG_TYPE_DISPO}, opts);
          const e = res.entregas.find(x => x.Id === precio.PrecioRAW.Entrega.Id);
          if (e) {
            this.entregaCtrl.setValue({Id: e.Id, Label: e.Entrega}, opts);
          }
        }
        this.negocioCtrl.updateValueAndValidity();
        this.entregaCtrl.updateValueAndValidity();
      }
    });
  }

  ngAfterViewInit() {
    // en afterviewInit preparamos los search, no nos suscribimos si no que usamos async pipe en el html
    this.$clienteOptions = this.clienteFormComp.$searchValue
      .debounceTime(750) // esperamos 750 ms para la busqueda
      .switchMap(searchTerm => {
        // si es >= a 3, devolvemos el observable de la busqueda de lo contrario retornamos un observable vacio
        return searchTerm.trim().length >= 3 ?
          this.searchService.searchCuentaAutocomplete(searchTerm.trim()) : of<ClienteViewModel[]>([]);
      })
      .map(arr => {
        // mapeamos la busqueda al formato de formulario
        return arr.map(cli => {
          const data = {
            Id: cli.Id,
            Label: cli.FullName,
            RawValue: JSON.stringify(cli),
          } as FormSelectItem;
          let cuentas = '';
          if (cli.CuentaAcopio && cli.CuentaAcopio.length) {
            cuentas = cli.CuentaAcopio.map(x => x.CuentaAcopio).filter(x => x !== 0).join(', ');
            if (cuentas.length) {
              data.ExtraData = cuentas;
            }
          }
          return data;
        });
      });


    this.$colaboradorOptions = this.colabFormComp.$searchValue
      .debounceTime(750)
      .switchMap(s => {
        return s.trim().length >= 3 ? this.searchService.search(s.trim(), SEARCH_TYPE_COLABORADOR) : of([]);
      })
      .map(arr => {
        console.log(arr);
        
        return arr.filter(usr => usr.Entity.UserId !== this.responsableCtrl.value.Id).map(usr =>
          ({Id: usr.Entity.UserId, Label: usr.Entity.UserFullName, RawValue: JSON.stringify(usr)}) as FormSelectItem
        );
      });

  }

  // dado que para un olvidado, debemos hacer una comprobacion previa, usamos esta funcion para separar esa logica
  async handleSubmit() {
    if (this.formInvalid() || this.submitLoading) {
      return;
    }

    // si la cosecha es futura en un negocio fisponible
    if (
      this.negocioCtrl.value.Id === NEG_TYPE_DISPO &&
      (JSON.parse(this.cosechaCtrl.value.RawValue) as CosechaViewModel).AnoHasta > moment().year()
    ) {

      let dialogRef: MdDialogRef<TextDialogComponent>;

      dialogRef = this.dialog.open(TextDialogComponent);
      dialogRef.componentInstance.title = 'Está intentando cargar un negocio disponible con una cosecha futura';
      dialogRef.componentInstance.message = '¿Está seguro que desea continuar?';
      dialogRef.componentInstance.positiveText = 'Aceptar';
      dialogRef.componentInstance.negativeText = 'Cancelar';

      // convertimos en una promesa para obtenerlo de forma sincronica
      const val = await dialogRef.afterClosed().toPromise();
      if (val !== true) {
        return;
      }
    }

    if (this.showOlvidado) {
      this.submitLoading = true;
      // en caso de ser un olvidado, verificamos que el negocio pueda crearse en primer lugar
      const payload = this.createPayload();
      if (payload) {
        this.negocioService.validarNegOlvidado(payload).take(1).subscribe(errorMessage => {
          if (!errorMessage.length) {
            // si no hay mensaje de error, es valido el negocio, procedemos con la creacion o edicion
            this.onSubmit();
            this.fechaOlvidoCtrl.setErrors(null);
          } else {
            this.snackbar.open(
              errorMessage,
              'Aceptar',
              {duration: 10000}
            );
            this.fechaOlvidoCtrl.setErrors({olvido: errorMessage});
            this.submitLoading = false;
          }
        });
      }
    } else {
      this.onSubmit();
    }
  }

  formInvalid(): boolean {
    this.form.markAsTouched();
    // marcamos como touched los hijos
    this.clienteCtrl.markAsTouched();
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
    // en lugar de usar simplemente form.invalid hacemos esto dado que si un control está deshabilitado pero es valido, dará error
    return Object.keys(this.form.controls)
      .map(key => this.form.controls[key].invalid)
      .some(x => x);
  }

  onSubmit() {
    this.submitLoading = true;
    const payload = this.createPayload();
    if (!payload) {
      return;
    }

    if (this.isEdit) {
      this.negocioService.editNegocio(payload).take(1).subscribe(() => {
        this.submitLoading = false;
        this.dialogRef.close(true);
      }, () => {
        this.snackbar.open('Ha ocurrido un error al editar el negocio.');
      });
    } else if (this.isClone) {
      this.negocioService.addNegocio(payload, true).take(1).subscribe(() => {
        this.submitLoading = false;
        this.dialogRef.close(true);
      }, () => {
        this.snackbar.open('Ha ocurrido un error al duplicar el negocio.');
      });
    } else {
      // nuevo negocio
      this.negocioService.addNegocio(payload).take(1).subscribe(() => {
        this.submitLoading = false;
        this.dialogRef.close(true);
      }, () => {
        this.snackbar.open('Ha ocurrido un error al crear el negocio.');
      });
    }
  }

  updateEntregaOptions(): void {
    if (!this.negocioCtrl.value) {
      // en caso de no haber ninguna entrega seleccionada colocamos un array vacío
      this.$entregaOptions = of([]);
      return;
    }

    this.$entregaOptions = this.getEntregaValueAsForm(this.negocioCtrl.value.Id);

  }

  getEntregaValueAsForm(negocioType: string): Observable<FormSelectItem[]> {
    switch (negocioType) {
      case NEG_TYPE_DISPO:
        return this.negocioDataService.$entregaDispo
          .map(x => {
            return x.map(y => ({Id: y.Id, Label: y.Entrega}) as FormSelectItem);
          });
      case NEG_TYPE_FORWARD:
        return this.negocioDataService.$posicionAll
          // solo Forward
          .map(arr => arr.filter(x => x.Ent_ID === ENT_FORWARD_ID))
          // filtramos por fecha de hoy y por primer dia del mes
          .map(arr => this.filterEntregaByTime(arr))
          .map(arr => arr.map(x => this.posicionToFormObj(x, true)));
      case NEG_TYPE_PAG_DIFE:
        return this.negocioDataService.$posicionAll
          // solo pago diferido
          .map(arr => arr.filter(x => x.Ent_ID === ENT_PAGO_DIFERIDO_ID))
          // filtramos los pagos diferidos anteriores a hoy
          .map(arr => {
            return arr.filter(posicion => {
              return moment(posicion.Posicion, 'DD/MM/YYYY').endOf('day').isSameOrAfter(moment().endOf('day'));
            });
          })
          // filtramos por fecha de hoy y por primer dia del mes
          .map(arr => this.filterEntregaByTime(arr))
          .map(arr => arr.map(x => this.posicionToFormObj(x, false)));
    }
  }

  filterEntregaByTime(arr: PosicionViewModel[]): PosicionViewModel[] {
    const firstDayMonthTime = moment().startOf('month').unix();
    return arr.filter(x => {
      // si no es el primer dia habil, quitamos el mes actual
      if (!this.firstBusinessDay && moment(x.Fecha).format('MM/YYYY') === moment().format('MM/YYYY')) {
        return true;
      }
      return firstDayMonthTime < moment(x.Fecha).unix();
    });
  }

  /**
   * Convierte un viewmodel de posicion en un objeto de formulario
   * @param obj El objeto a convertir
   * @param convertDate La fecha debe convertirse de fecha a formato de mes de 3 letras y año
   * */
  posicionToFormObj(obj: PosicionViewModel, convertDate: boolean): FormSelectItem {
    return {Id: obj.Id, Label: convertDate ? dateToMonthAndYearFormat(obj.Fecha.toString()) : obj.Posicion, RawValue: JSON.stringify(obj)};
  }

  // Validamos que los colabores seleccionado puedan ver el producto
  // (esto se puede mejorar con un EP que devuelva colaboradores con un producto)
  getAsyncValidarProductos(productoCtrl: AbstractControl, productoService: ProductoService) {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) {
      return of(null);
    }
    // recibimos del control un array de ids de usuario, los verificamos uno por uno
    const userIds = (control.value as FormSelectItem[]).map(x => x.Id.toString());
    if (!userIds.length || !productoCtrl.value || productoCtrl.value.Id === 0) {
      return of(null);
    }
    // ingresamos cada observable al array, se aplicamos un pipe que verifica que los productos visibles que posee, incluyan el seleccionado
    const observables = userIds.map(x => {
      return productoService.getProductosVisibles(x).take(1).map(x => x.includes(productoCtrl.value.Id));
    });
    // retornamos un fork que enviara solo una notificacion con el resultado de todos los observables en un array
    return Observable.forkJoin(observables)
      .map(x => {
        // todos los colaboradores deben poder ver el producto seleccionado
        return x.every(y => y) ? null : {producto: true};
      });
    };
  }


  createPayload(): NegocioSaveViewModel | null {
    if (this.formInvalid()) {
      return null;
    }

    const payload: NegocioSaveViewModel = {
      AcopioId: this.acopioCtrl.value.Id,
      Base: Number(this.baseCtrl.value.toString().replace(/[^0-9|,]/g, '').replace(',', '.')),
      ClienteId: this.clienteCtrl.value.Id,
      Colaboradores: (this.colaboradoresCtrl.value as FormSelectMultipleItem[]).map(x => Number(x.Id)),
      CosechaId: this.cosechaCtrl.value.Id,
      CustomNotification: false,
      EntregaId: 0, // se modifica mas abajo
      NegocioTipoPosicionId: 0,
      Fue_ID: this.isMobile ? 2 : 1, // fuente, 1 es web
      HorasNotificacion: null,
      MercadoId: this.mercadoCtrl.value.Id,
      // si es dolar a pesificar pasa a ser dolar
      MonedaId: this.monedaCtrl.value.Id === DOLAR_PESOS_ID ? DOLAR_ID : this.monedaCtrl.value.Id,
      Observaciones: this.observacionCtrl.value,
      // yerbas raras, la pesificacion se maneja por fuera de la base de datos de monedas
      Pesificar: this.monedaCtrl.value.Id === DOLAR_PESOS_ID ? 1 : 0,
      PrecioEspecial: this.isEdit ? this.negocioBase.PrecioEspecial : 0,
      PrecioIdea: this.isEdit && this.negocioBase.PrecioIdea !== null ? this.negocioBase.PrecioIdea : 0,
      PrecioOperacion: this.isEdit ? this.negocioBase.PrecioOperacion : null,
      ProductoId: this.productoCtrl.value.Id,
      TipoNegocioId: this.tipoCtrl.value.Id,
      Toneladas: Number(this.toneladasCtrl.value.toString().replace(/[^0-9|,]/g, '').replace(',', '.')),
      UserId: Number(localStorage.getItem('userId')),
      Vencimiento: this.vencimientoCtrl.value ? moment(this.vencimientoCtrl.value).unix() : moment().add(14, 'days').unix(),
    };
    if (this.isEdit) {
      payload.Id = this.negocioBase.Id;
      payload.Match = this.negocioBase.Match; // dato de control
      payload.FechaCreacion = moment.unix(this.negocioBase.Fecha).toDate();
    } else if (this.tipoCtrl.value.Id === NEG_OLVIDADO_ID) {
      payload.FechaCreacion = moment(this.fechaOlvidoCtrl.value).toDate();
    }

    // Entrega/Posicion y Negocio
    if (this.negocioCtrl.value.Id === NEG_TYPE_DISPO) {
      payload.EntregaId = this.entregaCtrl.value.Id;
      payload.NegocioTipoPosicionId = null;
    } else if (this.negocioCtrl.value.Id === NEG_TYPE_FORWARD) {
      payload.EntregaId = 3; // Id forward
      payload.NegocioTipoPosicionId = this.entregaCtrl.value.Id;
    } else if (this.negocioCtrl.value.Id === NEG_TYPE_PAG_DIFE) {
      payload.EntregaId = 4; // Id diferido
      payload.NegocioTipoPosicionId = this.entregaCtrl.value.Id;
    }
    return payload;
  }

  getTooltipSaldo(saldo: ClienteSaldoViewModel) {
    return `Tns Totales Pendientes:  ${this.tnsPipe.transform(saldo.TNTotalesPendientes)} \n
              Tns Certificadas:  ${this.tnsPipe.transform(saldo.TNCertificados)} \n
              Tns Sin Certificar:  ${this.tnsPipe.transform(saldo.TNSinCertificar)}`;
  }

  transformTns(value: any) {
    return this.tnsPipe.transform(value);
  }

  determineHeader() {
    let header = 'Nuevo';

    if (this.isEdit) {
      header = 'Editar';
    } else if (this.isClone) {
      header = 'Duplicar';
    }

    return `${header} Negocio`;
  }

  checkCuentaAcopioValid() {
    this.cuentaAcopioError = false;

    if (this.clienteCtrl.value && this.acopioCtrl.value) {
      const { CuentaAcopio } = JSON.parse(this.clienteCtrl.value.RawValue);
      const { Parent, Id} = JSON.parse(this.acopioCtrl.value.RawValue);

      if (CuentaAcopio) {
        const acopioId = Parent !== null ? Parent.Id : Id;
        this.cuentaAcopioError = CuentaAcopio.find(x => x.AcopíoID === acopioId).CuentaAcopio === 0;
      }
    }
  }

  determineFormIsValid() {
    return this.form.valid && !(this.clienteCtrl.value && this.cuentaAcopioError); 
  }
}

export interface NewNegocioData {
  isClone: boolean;
  isEdit: boolean;
  idNegocio: number;
  fromPrecio: Precio | null;
}



// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/components/negocios/new-negocio-responsive-dialog/new-negocio-responsive-dialog.component.ts